import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { inventoryApi } from '@/api/endpoints/inventory'
import { cn } from '@/lib/utils'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * The safety buffer, editable in the row (CHG-2026-09-menu-editor-redesign).
 *
 * It is the only quantity in the ledger the manager *sets* — on hand is the result of a counted
 * movement, reserved comes from open orders, incoming belongs to a delivery note, and sellable is
 * derived. So it is the only one that becomes a field, and the dashed underline is what says so.
 *
 * Commit and revert mirror the price field in the same row rather than inventing a second
 * pattern: Enter and blur commit, Escape reverts.
 */
export function BufferField({
  sku,
  restaurantId,
  menuId,
  value,
  canEdit,
}: {
  sku: string
  restaurantId: number
  menuId: number
  value: number
  canEdit: boolean
}) {
  const { t } = useTranslation('menus')
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(formatStockDisplay(value))
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!editing) setDraft(formatStockDisplay(value))
  }, [value, editing])

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const save = useMutation({
    mutationFn: (qty: number) =>
      inventoryApi.setSafetyBuffer(sku, restaurantId, qty),
    onSuccess: () => {
      toast.success(t('itemRow.bufferSaved'))
      queryClient.invalidateQueries({ queryKey: ['menus', menuId, 'contents'] })
      queryClient.invalidateQueries({
        queryKey: ['inventory', sku, restaurantId],
      })
    },
    onError: () => toast.error(t('itemRow.bufferSaveFailed')),
  })

  const commit = () => {
    setEditing(false)
    const next = Number(draft.trim())
    if (!Number.isInteger(next) || next < 0) {
      setDraft(formatStockDisplay(value))
      return
    }
    if (next === value) return
    save.mutate(next)
  }

  if (!canEdit) {
    return (
      <div className="text-right text-sm tabular-nums text-muted-foreground">
        {value ? `−${formatStockDisplay(value)}` : '—'}
      </div>
    )
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        inputMode="numeric"
        aria-label={t('itemRow.bufferAria')}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') {
            setDraft(formatStockDisplay(value))
            setEditing(false)
          }
        }}
        className="h-7 w-full rounded-md border border-input bg-background px-1 text-right text-sm tabular-nums outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
      />
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={t('itemRow.bufferAria')}
          // No justify-self here: this button is not a grid item — the row wraps it — so that
          // property would silently do nothing and the value would sit left of every other
          // figure in the ledger. The wrapper right-aligns it as the inline element it is.
          className={cn(
            'text-sm tabular-nums text-muted-foreground',
            'border-b border-dashed border-muted-foreground/40',
            save.isPending && 'opacity-60',
          )}
        >
          {value ? `−${formatStockDisplay(value)}` : '—'}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">
        {t('itemRow.bufferHint')}
      </TooltipContent>
    </Tooltip>
  )
}
